import React, { useMemo, useState } from 'react';
import { pick } from 'ramda';

import { Checkbox } from '@interlayer/js/components/common/Fields/Checkbox';
import I18n from '@interlayer/js/components/common/I18n';
import { LocalPopup } from '@interlayer/js/components/common/Popup/LocalPopup';
import { WebContent } from '@interlayer/js/components/system/WebContent/WebContent';

import { useTranslations } from '@core/js/hooks/i18n';
import { useDeviceSelector } from '@core/js/store/hooks';

import { useIsRetailUpgrade } from '@interlayer/js/app/components/main/Registration/components/templates/RetailReg/hooks/useIsRetailUpgrade';
import { useRegistrationContext } from '@interlayer/js/app/components/main/Registration/hocs/context';
import { useIsMultiColumn } from '@interlayer/js/app/components/main/Registration/hooks/storeData/useIsMultiColumn';
import { VALIDATION_FIELDS } from '@interlayer/js/app/hooks/useValidationRules/constants';
import { useRegistrationSelector } from '@interlayer/js/app/store/hooks/useRegistrationSelector';
import { clsx } from '@interlayer/js/app/utils/clsx.util';

import { PRIVACY_POLICY, TERMS_AND_CONDITIONS } from './constants';

import style from '../../PreferencesFields.scss';

export const TermsAgreeMessage: React.FC = () => {
    const isMultiColumn = useIsMultiColumn();
    const { isRetailWrapper } = useRegistrationContext();
    const [popupName, setPopupName] = useState('');
    const { config: { termsAndConditionsId, privacyPolicyWebContentID } } = useRegistrationSelector();
    const t = useTranslations('playtech.mobile.system.registration.preferences.');
    const { browserName, osVersion, os } = useDeviceSelector(pick(['browserName', 'osVersion', 'os']));

    const isIosSafari = useMemo(() => {
        return os === 'ios' && browserName === 'safari' && parseInt(osVersion) <= 15;
    }, [browserName, os, osVersion]);

    const privacyPolicy = privacyPolicyWebContentID || PRIVACY_POLICY;
    const termsAndConditions = termsAndConditionsId || TERMS_AND_CONDITIONS;

    const handlerSetPopupName = (name = '') => (event: React.MouseEvent<HTMLSpanElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setPopupName(name);
    };

    return (
        <div className={style.agreeMessage}>
            <I18n t={t} tKey="agree" />{' '}

            <span
                className={style.ruleLink}
                onClick={handlerSetPopupName(privacyPolicy)}
            >
                <I18n t={t} tKey="privacyPolicy" />
            </span>{' '}

            <I18n t={t} tKey="agreeAdditional" />{' '}

            <span
                className={style.ruleLink}
                onClick={handlerSetPopupName(termsAndConditions)}
            >
                <I18n t={t} tKey="termsConditions" />
            </span>

            {popupName && (
                <LocalPopup
                    fullscreen
                    withLogo={!isMultiColumn && !isRetailWrapper}
                    className={clsx(style[`termsModal${isMultiColumn ? 'isMultiColumn' : ''}`], style.popup)}
                    classNames={{
                        close: isIosSafari && style.close,
                        inner: style[`termsModalInner${isMultiColumn ? 'isMultiColumn' : ''}`]
                    }}
                    title={t(`modal.title.${popupName === privacyPolicy ? 'privacyPolicy' : 'termsAndConditions'}`)}
                    onClose={handlerSetPopupName()}
                    isTextCenter={isMultiColumn}
                >
                    <WebContent
                        id={popupName}
                        className={style.webContent}
                    />
                </LocalPopup>
            )}
        </div>
    );
};

export const TermsCheckbox: React.FC = () => {
    const isRetailUpgrade = useIsRetailUpgrade();
    const { isUpgrade } = useRegistrationSelector();

    if (isUpgrade || isRetailUpgrade) {
        return null;
    }

    return (
        <Checkbox
            reverse
            isTransparent
            kind="toggle"
            type="checkbox"
            name={VALIDATION_FIELDS.terms}
            label={<TermsAgreeMessage />}
            classNames={{
                checkbox: style.checkboxTerms,
                label: style.agreeLabel
            }}
        />
    );
};
